<script setup lang="ts">
import { computed } from "vue";
import DocumentDimensionDTO = App.Data.DocumentDimensionDTO;
import DimensionDO = App.Data.DimensionDO;
import { useSegmentStore } from "@/modules/explorer/SegmentStore";
import { useExplorerStore } from "@/modules/explorer/store";
import {
	verticalHasSegmentOption,
	verticalOrDimensionSupportsLostCustomers,
} from "@/modules/explorer/PropertyStore";

const model = defineModel<DocumentDimensionDTO>();
const {
	dimensions,
	showSortDirection = false,
	disableSortDirection = false,
} = defineProps<{
	dimensions: DimensionDO[];
	showSortDirection?: boolean;
	disableSortDirection?: boolean;
}>();

const invertSortOrderOverride = computed<boolean | null>({
	get() {
		return model.value.invert_sort_order;
	},
	set(value) {
		model.value = {
			...model.value,
			invert_sort_order: value,
		};
	},
});

const selectedBucketInvertsSortOrder = computed(() => {
	if (
		model.value.type === "hierarchy" ||
		model.value.type === "segment" ||
		verticalHasSegmentOption(model.value.name_clean)
	) {
		return false;
	}

	const buckets =
		dimensions.find((dimension) => dimension.id === model.value.id)
			?.dimension_buckets ?? [];

	return !!buckets.find((bucket) => bucket.id === dimensionBucketId.value)
		?.has_inverted_sort_order;
});

const effectiveInvertSortOrder = computed(
	() => invertSortOrderOverride.value ?? selectedBucketInvertsSortOrder.value
);

const hierarchyDefinitionId = computed({
	get() {
		return model.value.hierarchy_definition_id;
	},
	set(value) {
		model.value = {
			...model.value,
			hierarchy_definition_id: value,
		};
	},
});

const dimensionBucketId = computed({
	get() {
		return model.value.dimension_bucket_id;
	},
	set(value) {
		model.value = {
			...model.value,
			dimension_bucket_id: value,
		};
	},
});

const segmentId = computed({
	get() {
		return model.value.segment_id;
	},
	set(value) {
		model.value = {
			...model.value,
			segment_id: value,
		};
	},
});

const backingDimensionField = computed(() => {
	if (model.value.type === "hierarchy") {
		return hierarchyDefinitionId;
	}

	if (
		model.value.type === "segment" ||
		verticalHasSegmentOption(model.value.name_clean)
	) {
		return segmentId;
	}

	return dimensionBucketId;
});

const dimensionSpecificationId = computed({
	get() {
		return backingDimensionField.value.value;
	},
	set(value) {
		backingDimensionField.value.value = value;
	},
});

const dimensionSpecificationOptions = computed(() => {
	if (model.value.type === "hierarchy") {
		const hierarchies =
			dimensions.find((dimension) => dimension.id === model.value.id)
				?.hierarchy_definitions ?? [];
		return useExplorerStore().document.view_tab !== "table"
			? hierarchies
			: [
					...hierarchies,
					{
						id: null,
						name: "Raw Value",
						dimension_id: model.value.id,
						is_default: false,
						should_auto_label: false,
					},
			  ];
	}

	if (
		model.value.type === "segment" ||
		verticalHasSegmentOption(model.value.name_clean)
	) {
		if (verticalOrDimensionSupportsLostCustomers(model.value.name_clean)) {
			return useSegmentStore().segments;
		}
		return useSegmentStore().segments.filter(
			(segment) => !segment.has_dependency_on_lost_customer_segment
		);
	}

	const buckets =
		dimensions.find((dimension) => dimension.id === model.value.id)
			?.dimension_buckets ?? [];

	// add a fake bucket with id as null to get the raw value (only for data table)
	return useExplorerStore().document.view_tab !== "table"
		? buckets
		: [
				...buckets,
				{
					id: null,
					name: "Raw Value",
					dimension_id: model.value.id,
					is_default: false,
					should_auto_label: false,
				},
		  ];
});

const itemTitle = computed(() => {
	if (
		model.value.type === "segment" ||
		verticalHasSegmentOption(model.value.name_clean)
	) {
		return "title";
	}

	return "name";
});
</script>

<template>
	<div class="d-flex ga-2">
		<v-select
			hide-details
			density="compact"
			v-model="dimensionSpecificationId"
			:items="dimensionSpecificationOptions"
			item-value="id"
			:item-title="itemTitle"
		/>
		<v-btn
			v-if="showSortDirection"
			height="32"
			width="32"
			rounded
			color="background"
			:disabled="disableSortDirection"
			:icon="`mdi-sort-${
				effectiveInvertSortOrder ? 'descending' : 'ascending'
			}`"
			v-tooltip="
				`Sort order: ${effectiveInvertSortOrder ? 'descending' : 'ascending'}`
			"
			@click="invertSortOrderOverride = !effectiveInvertSortOrder"
		/>
	</div>
</template>

<style scoped lang="scss"></style>
